/**
 * Button Component
 *
 * A versatile button component with multiple variants, sizes, and states.
 * Includes comprehensive logging for user interactions.
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/utils';
import { Logger, LogCategory } from '@/utils/logger';
import { Loader2 } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
}

// ============================================================================
// Styles
// ============================================================================

const baseStyles = `
  inline-flex items-center justify-center
  font-medium rounded-lg
  transition-all duration-200
  focus:outline-none focus:ring-2 focus:ring-offset-2
  disabled:opacity-50 disabled:cursor-not-allowed
`;

const variantStyles: Record<ButtonVariant, string> = {
  primary: `
    bg-primary-600 text-white
    hover:bg-primary-700
    focus:ring-primary-500
    active:bg-primary-800
  `,
  secondary: `
    bg-slate-100 text-slate-900
    hover:bg-slate-200
    focus:ring-slate-500
    active:bg-slate-300
    dark:bg-slate-700 dark:text-slate-100
    dark:hover:bg-slate-600
  `,
  outline: `
    border-2 border-slate-300 text-slate-700
    hover:bg-slate-50
    focus:ring-primary-500
    active:bg-slate-100
    dark:border-slate-600 dark:text-slate-300
    dark:hover:bg-slate-800
  `,
  ghost: `
    text-slate-600
    hover:bg-slate-100
    focus:ring-slate-500
    active:bg-slate-200
    dark:text-slate-400
    dark:hover:bg-slate-800
  `,
  danger: `
    bg-danger-600 text-white
    hover:bg-danger-700
    focus:ring-danger-500
    active:bg-danger-800
  `,
  success: `
    bg-success-600 text-white
    hover:bg-success-700
    focus:ring-success-500
    active:bg-success-800
  `,
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2.5',
};

// ============================================================================
// Component
// ============================================================================

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled,
      onClick,
      children,
      ...props
    },
    ref
  ) => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      Logger.debug(LogCategory.UI, 'Button clicked', 'Button', {
        variant,
        size,
        disabled: disabled || isLoading,
        buttonText: typeof children === 'string' ? children : undefined,
      });

      if (onClick && !disabled && !isLoading) {
        onClick(e);
      }
    };

    return (
      <button
        ref={ref}
        className={cn(
          baseStyles,
          variantStyles[variant],
          sizeStyles[size],
          fullWidth && 'w-full',
          className
        )}
        disabled={disabled || isLoading}
        onClick={handleClick}
        {...props}
      >
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading...</span>
          </>
        ) : (
          <>
            {leftIcon && <span className="shrink-0">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="shrink-0">{rightIcon}</span>}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

// ============================================================================
// Icon Button Variant
// ============================================================================

export interface IconButtonProps extends Omit<ButtonProps, 'leftIcon' | 'rightIcon' | 'children'> {
  icon: ReactNode;
  'aria-label': string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, className, size = 'md', ...props }, ref) => {
    const iconSizeStyles: Record<ButtonSize, string> = {
      sm: 'h-8 w-8',
      md: 'h-10 w-10',
      lg: 'h-12 w-12',
    };

    return (
      <Button
        ref={ref}
        className={cn('!p-0', iconSizeStyles[size], className)}
        size={size}
        {...props}
      >
        {icon}
      </Button>
    );
  }
);

IconButton.displayName = 'IconButton';
